import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { mergeClasses, FluentProvider, webLightTheme, Button, Divider } from '@fluentui/react-components';
import { Overlay, MessageBar, MessageBarType } from '@bingads-webui-react/fabric-control';
import { Dismiss24Regular, VideoRegular, Send16Regular, ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Drawer,
  DrawerFooter,
} from '@fluentui/react-drawer';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';
import { PreviewClipchampVideoModal } from '@bingads-webui-campaign-react/clipchamp-player';
import { useRecommendations } from '@bingads-webui-campaign-react/perf-max-recommendation';

import { VideoTemplateSummaryBarControl } from '../components/video-template-summary-bar-control';
import { useStyles } from './style';
import { EditVideoNavigationView } from '../navigation-view';
import { EditVideoAssetsView } from './assets-editor';
import { EditVideoStyleView } from './style-editor';
import { EditVideoAudioView } from './audio-editor';
import { ScenePreview } from './scene-preview';
import { StoryBoardView } from './story-board';
import { ViewName, shortHeadlineType } from '../util';
import { useNavigationState } from '../use-navigation-state';
import { useEditVideoAdsViewState } from '../state';

export const EditVideoPageComponent = ({
  i18n,
  isOpen,
  setIsOpen,
  videoMetaData,
  cid,
  aid,
  oDataConfig,
  finalUrl,
  activity,
  copilotDataService,
  onChangeImage,
  onChangeLogo,
  logger,
  isCTV,
  preferenceService,
  serverContext,
  perfMarker,
  isDialog,
  imageAnimationAvailable,
  imageAnimationEnabled,
  permissions,
  loadClipchampPlayer,
  brandKit,
  templateDetail,
  isLoadingBrandKit,
  onApply,
  instrumentation,
  showVideoTemplateSummaryBarControl,
  rawAudioList,
}) => {
  const classes = useStyles();
  const { currentView, setCurrentView } = useNavigationState();
  const [showPreviewVideoModal, setShowPreviewVideoModal] = useState(false);
  const ref = React.useRef(null);

  const {
    handleCopilotBannerFetchSucceeded,
  } = useRecommendations({
    cid,
    aid,
    campaignId: -1,
    permissions,
    finalUrl,
    isDisplayPlus: false,
    enableCopilotRecommendations: true,
    instrumentation,
    isMMA: false,
    isSignup: false,
    skipBusinessNameRecommendation: true,
  });

  const {
    selectedSceneId,
    onSceneSelected,
    scenes,
    onImageUpdated,
    onLogoUpdated,
    onScenesUpdated,
    onFontsUpdated,
    onPalettesUpdated,
    video,
    videoSettings,
    isGeneratingVideo,
    setVideoSettings,
    fontsState,
    palettesState,
    audioState,
    setAudioState,
    handleGenerate,
    recommendedAssets,
    isLoadingRecommendedAssets,
    generateError,
    setGenerateError,
    videoUrl,
    copilotRecommendedAssets,
    ariaLiveVideoGenerateMessage,
    addTask,
    cancelTask,
    updatedClips,
    inProgressTaskList,
    onAnimationToggled,
    animationToggleState,
  } = useEditVideoAdsViewState({
    onApply,
    videoMetaData,
    copilotDataService,
    logger,
    i18n,
    isCTV,
    finalUrl,
    handleCopilotBannerFetchSucceeded,
    activity,
    showVideoTemplateSummaryBarControl,
    imageAnimationEnabled,
  });

  useEffect(() => {
    const parentContainer = ref && ref.current && ref.current.parentElement;
    if (parentContainer) {
      parentContainer.style.zIndex = '1000';
    }
  }, [ref]);

  useEffect(() => {
    // For tab-focus accessibility on back buton
    const backButton = document.querySelector('.ui-next-summary-bar .back-button');
    if (backButton) {
      backButton.focus();
    }
  }, []);

  const onHandleApply = useCallback((isProject = false) => {
    const newVideo = { ...video };
    newVideo.src = video.src || video.url;

    onApply(newVideo, isProject, updatedClips);
    setIsOpen(false);
    if (activity && activity.signal) {
      const message = `[VideoAdsEditor] - Edit Video - Save - TemplateId:${videoMetaData.template.templateId}, AdStudio:${showVideoTemplateSummaryBarControl}`;
      activity.signal(message);
    }
  }, [onApply, setIsOpen, videoMetaData, video, activity, showVideoTemplateSummaryBarControl]);

  const handleTextListChange = useCallback((textList) => {
    const updatedScenes = scenes.map((scene) => {
      if (scene.id === selectedSceneId) {
        return {
          ...scene,
          updating: {
            ...scene.updating,
            text: textList,
          },
        };
      }
      return scene;
    });
    onScenesUpdated(updatedScenes);
  }, [scenes, selectedSceneId, onScenesUpdated]);

  const deepClone = useCallback((cloneIt) => {
    if (cloneIt === null || typeof cloneIt !== 'object') return cloneIt;
    if (Array.isArray(cloneIt)) {
      return cloneIt.map(item => deepClone(item));
    }

    const result = {};
    Object.keys(cloneIt).forEach((key) => {
      result[key] = deepClone(cloneIt[key]);
    });

    return result;
  });

  useEffect(() => {
    if (videoMetaData && videoMetaData.template && videoMetaData.template.scenes && videoMetaData.template.scenes.length > selectedSceneId) {
      const scene = videoMetaData.template.scenes[selectedSceneId];
      if (scene && scene.textData && scene.textData.length > 0 && finalUrl && copilotRecommendedAssets) {
        const selectedSceneTextData = scene.textData;
        const textData = selectedSceneTextData && selectedSceneTextData[0];
        const isBusinessName = textData && textData.hint === shortHeadlineType.BusinessName;
        const shortHeadlines = copilotRecommendedAssets.RecommendedAssets && copilotRecommendedAssets.RecommendedAssets.ShortHeadlines;

        if (textData && textData.maxLength > 0 && shortHeadlines) {
          const cloneCopilotRecommendedAssets = deepClone(copilotRecommendedAssets);
          const newTheme = cloneCopilotRecommendedAssets.RecommendedAssets.Themes.map((item) => {
            if (item.ShortHeadlines) {
              // eslint-disable-next-line no-param-reassign
              item.ShortHeadlines = item.ShortHeadlines.filter(shortHeadline => shortHeadline.Text.length <= (isBusinessName ? 0 : textData.maxLength));
            }
            return item;
          });

          const filteredRecommendations = {
            RecommendedAssets: cloneCopilotRecommendedAssets.RecommendedAssets,
            Logo: cloneCopilotRecommendedAssets.Logo,
            Cta: cloneCopilotRecommendedAssets.Cta,
            IsCustomerIdInExclusionList: cloneCopilotRecommendedAssets.IsCustomerIdInExclusionList,
          };

          filteredRecommendations.RecommendedAssets.ShortHeadlines = shortHeadlines.filter(item => item.Text.length <= (isBusinessName ? 0 : textData.maxLength));
          filteredRecommendations.RecommendedAssets.Themes = newTheme;

          handleCopilotBannerFetchSucceeded(finalUrl, filteredRecommendations);
        }
      }
    }
  }, [videoMetaData, selectedSceneId, copilotRecommendedAssets, finalUrl, handleCopilotBannerFetchSucceeded]);


  const onCloseVideoPreview = useCallback(() => {
    setShowPreviewVideoModal(false);
  }, [setShowPreviewVideoModal]);

  const handleSelectNextScene = useCallback(() => {
    const selectedScene = scenes[selectedSceneId + 1];
    onSceneSelected(selectedScene);
  }, [onSceneSelected, selectedSceneId, scenes]);

  const handleSelectPreviousScene = useCallback(() => {
    const selectedScene = scenes[selectedSceneId - 1];
    onSceneSelected(selectedScene);
  }, [onSceneSelected, selectedSceneId, scenes]);

  const isPreviousDisabled = useMemo(() => selectedSceneId <= 0, [selectedSceneId]);
  const isNextDisabled = useMemo(() => selectedSceneId >= scenes.length - 1, [scenes, selectedSceneId]);

  const isHorizontalVideo = useMemo(() => {
    const data = video && video.template;
    if (data && data.width && data.height) {
      return data.width > data.height;
    }
    return true;
  }, [video]);

  const videoTemplate = useMemo(() => (video && video.template ? video.template : null), [video]);

  const isAnimationProcessing = useMemo(() => !_.isEmpty(inProgressTaskList), [inProgressTaskList]);

  const VideoEditContent = useMemo(() => (
    <FluentProvider theme={webLightTheme} style={{ height: '100%' }}>
      <h1 className={classes.screenReaderOnly}>{i18n.getString(_TL_('Edit Video'))}</h1>
      <div className={classes.screenReaderOnly} aria-live="polite">
        {ariaLiveVideoGenerateMessage}
      </div>
      <div className={classes.containerWrapper}>
        {isGeneratingVideo && (
          <Overlay styles={{ root: { zIndex: 1 } }}>
            <AssetLibrarySpinner className="page-load-spinner" useCustomSize />
          </Overlay>
        )}
        {videoMetaData && videoMetaData.template && (
          <div className={mergeClasses('edit-video-page-container', classes.editVideoPage, 'edit-video-page', classes.containerWrapper)}>
            {showPreviewVideoModal && <PreviewClipchampVideoModal
              videoUrl={videoUrl}
              show={showPreviewVideoModal}
              onClose={onCloseVideoPreview}
              i18n={i18n}
              logger={logger}
              loadClipchampPlayer={loadClipchampPlayer}
              onShowChange={setShowPreviewVideoModal}
              isAnimationProcessing={isAnimationProcessing}
            />}
            {showVideoTemplateSummaryBarControl && (<VideoTemplateSummaryBarControl
              i18n={i18n}
              videoUrl={videoUrl}
              showPreviewVideoModal={showPreviewVideoModal}
              setShowPreviewVideoModal={setShowPreviewVideoModal}
              onSaveVideo={onHandleApply}
              metadata={videoMetaData}
              activity={activity}
              permissions={permissions}
            />
            )}
            <div className={mergeClasses(classes.navigationView, 'navigation-view')} >
              <EditVideoNavigationView
                i18n={i18n}
                currentView={currentView}
                setCurrentView={setCurrentView}
              />
            </div>
            <div className={mergeClasses('edit-video-actions-wrapper', classes.actionsWrapper)} style={{ borderRadius: '10px' }}>
              {currentView === ViewName.AssetsView && (
                <div className={classes.topNavigationParent}>
                  <div className={classes.topNavigationLeft}>{i18n.getString(_TL_('Edit scene '))}{selectedSceneId + 1}{i18n.getString(_TL_(' of '))}{scenes.length}</div>
                  <div className={classes.topNavigationRight}>
                    <Button
                      className={mergeClasses('previous-scene-button', classes.noBorderStyle)}
                      icon={<ChevronLeftRegular />}
                      disabled={isPreviousDisabled}
                      onClick={handleSelectPreviousScene}
                      aria-label={i18n.getString(_TL_('Select previous scene.'))}
                    />
                    <Button
                      className={mergeClasses('next-scene-button', classes.noBorderStyle)}
                      icon={<ChevronRightRegular />}
                      disabled={isNextDisabled}
                      onClick={handleSelectNextScene}
                      aria-label={i18n.getString(_TL_('Select next scene.'))}
                    />
                  </div>
                </div>
              )}
              <div className={mergeClasses('edit-video-actions-wrapper', classes.actionsWrapperContent)}>
                <div>
                  {
                    currentView === ViewName.AssetsView &&
                    <EditVideoAssetsView
                      i18n={i18n}
                      aid={aid}
                      cid={cid}
                      logger={logger}
                      activity={activity}
                      copilotDataService={copilotDataService}
                      oDataConfig={oDataConfig}
                      onTextListChange={handleTextListChange}
                      onChangeImage={onChangeImage}
                      setVideoSettings={setVideoSettings}
                      scenes={scenes}
                      template={videoTemplate}
                      selectedSceneId={selectedSceneId}
                      onSceneSelected={onSceneSelected}
                      onImageUpdated={onImageUpdated}
                      onLogoUpdated={onLogoUpdated}
                      videoSettings={videoSettings}
                      onChangeLogo={onChangeLogo}
                      preferenceService={preferenceService}
                      serverContext={serverContext}
                      perfMarker={perfMarker}
                      permissions={permissions}
                      recommendedAssets={recommendedAssets}
                      isLoadingRecommendedAssets={isLoadingRecommendedAssets}
                      brandKit={brandKit}
                      isLoadingBrandKit={isLoadingBrandKit}
                      finalUrl={finalUrl}
                      imageAnimationAvailable={imageAnimationAvailable}
                      imageAnimationEnabled={imageAnimationEnabled}
                      addTask={addTask}
                      cancelTask={cancelTask}
                      updatedClips={updatedClips}
                      inProgressTaskList={inProgressTaskList}
                      onAnimationToggled={onAnimationToggled}
                      animationToggleState={animationToggleState}
                    />
                  }
                  {
                    currentView === ViewName.StyleView &&
                    <EditVideoStyleView
                      i18n={i18n}
                      fontsState={fontsState}
                      onFontsUpdated={onFontsUpdated}
                      palettesState={palettesState}
                      onPalettesUpdated={onPalettesUpdated}
                      brandKit={brandKit}
                    />
                  }
                  {
                    currentView === ViewName.AudioView &&
                    <EditVideoAudioView
                      i18n={i18n}
                      audioState={audioState}
                      setAudioState={setAudioState}
                      rawAudioList={rawAudioList}
                    />
                  }
                  <div className="message-bar">
                    {
                      generateError &&
                      <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline
                        onDismiss={() => setGenerateError(undefined)}
                        dismissButtonAriaLabel="Close"
                      >
                        {generateError}
                      </MessageBar>
                    }
                  </div>
                </div>
              </div>
              <div className={mergeClasses(classes.actionBarFooter, 'generate-video-button-container')}>
                <div>
                  <Divider />
                </div>
                <div className={mergeClasses(classes.generateButtonContainer, 'generate-video-button-container')}>
                  <Button
                    icon={<Send16Regular />}
                    appearance="primary"
                    onClick={handleGenerate}
                    className={mergeClasses(classes.generateButton, 'generate-video-button')}
                  >
                    {i18n.getString(_TL_('View updated scenes'))}
                  </Button>
                </div>
              </div>
            </div>
            <div className={mergeClasses(classes.previewContainer, 'edit-video-page-preview-container')}>
              <div className={mergeClasses(classes.scenePreview, 'edit-video-page-preview', classes.containerWrapper)}>
                <ScenePreview
                  selectedSceneId={selectedSceneId}
                  scenes={scenes}
                />
              </div>
              <div className={mergeClasses(isHorizontalVideo ? classes.storyBoardContainerHorizontal : classes.storyBoardContainerVertical, 'edit-video-page-storyboard-container')}>
                <StoryBoardView
                  i18n={i18n}
                  scenes={scenes}
                  selectedSceneId={selectedSceneId}
                  onSceneSelected={onSceneSelected}
                  videoMetadata={video}
                  templateDetail={templateDetail}
                  isHorizontalVideo={isHorizontalVideo}
                />
              </div>
            </div>
          </div>
        )}
        {
          !isGeneratingVideo && (!videoMetaData || !videoMetaData.template) &&
          <div>
            <div className="message-bar">
              <MessageBar messageBarType={MessageBarType.error} >
                {i18n.getString(_TL_('An error occured while loading the video.'))}
              </MessageBar>
            </div>
          </div>
        }
      </div>
    </FluentProvider>
  ), [permissions, isGeneratingVideo, classes, aid, cid, i18n, oDataConfig, onChangeImage, onChangeLogo, onSceneSelected, preferenceService, perfMarker, selectedSceneId, scenes, currentView, serverContext, setCurrentView, video, videoSettings, fontsState, palettesState, onFontsUpdated, onPalettesUpdated, audioState, setAudioState, handleGenerate, onImageUpdated, onLogoUpdated, setVideoSettings, handleTextListChange, recommendedAssets, isLoadingRecommendedAssets, brandKit, isLoadingBrandKit]);

  return (
    isDialog ? (
      <FluentProvider theme={webLightTheme} style={{ height: '100%' }}>
        <Drawer
          type="overlay"
          modalType="modal"
          open={isOpen}
          position="end"
          size="large"
          ref={ref}
          separator
          className={mergeClasses('video-ads-editor-panel', classes.videoEditor)}
        >
          <DrawerHeader>
            <DrawerHeaderTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label={i18n.getString(_TL_('Close'))}
                  icon={<Dismiss24Regular />}
                  onClick={() => setIsOpen(false)}
                />
              }
            >
              {i18n.getString(_TL_('Edit your video'))}
            </DrawerHeaderTitle>
          </DrawerHeader>
          <DrawerBody
            styles={{
              root: {
                paddingLeft: '0px',
                paddingRight: '0px',
              },
            }}
          >
            <div className={mergeClasses(classes.leftPanelTop, classes.containerWrapper)}>
              {VideoEditContent}
            </div>
          </DrawerBody>
          <DrawerFooter>
            <div className={classNames(classes.bottom, 'view-bottom')}>
              <div className="button-container">
                <Button
                  appearance="primary"
                  className="apply-button"
                  onClick={onHandleApply}
                >
                  {i18n.getString(_TL_('Apply'))}
                </Button>
                <Button
                  className="cancel-button"
                  onClick={() => {
                    setIsOpen(false);
                    logger.logTrace('Copilot video editor: Cancel button clicked');
                  }}
                >
                  {i18n.getString(_TL_('Cancel'))}
                </Button>
                <Button
                  icon={<VideoRegular />}
                  className="preview-button"
                  onClick={() => {
                    setShowPreviewVideoModal(true);
                    if (activity && activity.signal) {
                      // For recommendation opportunity logging
                      const opportunityId = videoMetaData.opportunityId || '';
                      const message = `[VideoAdsEditor] - Edit Video - Preview - TemplateId:${videoMetaData.template.templateId}, AdStudio:${showVideoTemplateSummaryBarControl}, OpportunityId:${opportunityId}`;
                      activity.signal(message);
                    }
                  }}
                >
                  {i18n.getString(_TL_('Preview video'))}
                </Button>
              </div>
            </div>
          </DrawerFooter>
        </Drawer>
      </FluentProvider>
    ) : (
      <div className={classes.containerWrapper}>
        {VideoEditContent}
      </div>
    )
  );
};

EditVideoPageComponent.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  videoMetaData: PropTypes.shape({
    images: PropTypes.arrayOf(PropTypes.string),
    text: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  finalUrl: PropTypes.string.isRequired,
  activity: PropTypes.shape({
    signal: PropTypes.func,
  }).isRequired,
  onChangeImage: PropTypes.func.isRequired,
  onChangeLogo: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func.isRequired,
  }).isRequired,
  dataService: PropTypes.shape({}).isRequired,
  copilotDataService: PropTypes.shape({}).isRequired,
  isCTV: PropTypes.bool,
  preferenceService: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  isDialog: PropTypes.bool,
  imageAnimationEnabled: PropTypes.bool,
  imageAnimationAvailable: PropTypes.bool,
  permissions: PropTypes.shape({}).isRequired,
  loadClipchampPlayer: PropTypes.func.isRequired,
  brandKit: PropTypes.shape({
    palettes: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string,
      HexCode: PropTypes.string,
      isBrandKitColor: PropTypes.bool,
    })),
  }).isRequired,
  templateDetail: PropTypes.shape({
    templateId: PropTypes.string,
    templateName: PropTypes.string,
  }).isRequired,
  isLoadingBrandKit: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  showVideoTemplateSummaryBarControl: PropTypes.bool.isRequired,
};

EditVideoPageComponent.defaultProps = {
  isCTV: false,
  isDialog: false,
  imageAnimationEnabled: false,
  imageAnimationAvailable: false,
};
